<!-- eslint-disable vue/multi-word-component-names -->
<!-- The House of Cavanaugh View -->
<template>
  <div class="break"></div>
  <page-layout left right header="The House of Cavanaugh" :hook="hook">
    <template #left>
      <book-menu />
    </template>
    <template #right>
      <book-content :book="bookCover" :paragraphs="introParagraphs" />
      <divider />
      <p class="other-paragraph">
        Revisiting a family from Dugan’s story collection,
        <em>So Much a Part of You</em>, <em>The House of Cavanaugh</em> explores
        the flaws of even the most seemingly stable and unblemished families,
        and how the ripple effects of a long-buried secret have the power to
        strengthen or destroy the closest ties between family and friends.
      </p>
      <divider />
      <h4>Purchase the Book</h4>
      <div class="buttonBox">
        <Button label="Amazon" :link="buttonLinks.amazon" />
        <Button label="Powell's Books" :link="buttonLinks.powells" />
        <Button label="Target" :link="buttonLinks.target" />
      </div>
      <divider />
      <book-praise header="Praise" :praises="bookPraise" />
    </template>
  </page-layout>
</template>

<script>
// import components
import PageLayout from "@/components/global-styles/layout/PageLayout.vue";
import BookMenu from "@/components/global-styles/default/BookMenu.vue";
import BookContent from "@/components/global-styles/layout/BookContent.vue";
import Divider from "@/components/global-styles/default/Divider.vue";
import Button from "@/components/global-styles/components/Button.vue";
import BookPraise from "@/components/global-styles/layout/BookPraise.vue";

// import images
import thocCover from "@/assets/images/book-covers/THOCCover.jpg";

export default {
  name: "THOCView",
  components: {
    PageLayout,
    BookMenu,
    BookContent,
    Divider,
    Button,
    BookPraise,
  },
  data() {
    return {
      bookCover: thocCover,
      introParagraphs: [
        `In New York City in 1964, Joan Cavanaugh, an uncertain young wife and mother, and Peter ‘Hutch’ Hutchinson have a brief but passionate affair, yet during their almost 30 years of marriage, Joan and her husband Graham successfully raise their three daughters, Ceci, Anne and Carolyn. When Joan dies of metastatic breast cancer in 1989, she takes the truth of Anne’s paternity to her grave.`,
        `In 2014 in Portland, Carolyn and Julia are next-door neighbors and the closest of friends. During the Thanksgiving holiday weekend, Hutch travels from New York to visit his youngest daughter and her family. Julia’s parents, Hutch and Alice, have recently relocated to Portland from the East Coast, and by the time the carefully choreographed introduction of the women’s respective parents takes place, Hutch has realized that the family his lover left behind is now significant in his own daughter’s life.`,
        `What unfolds following Hutch’s discovery upends both families and threatens the foundation of two women’s established friendship.`,
      ],
      buttonLinks: {
        amazon:
          "https://www.amazon.com/House-Cavanaugh-Polly-Dugan/dp/1960573462/ref=sr_1_1?crid=1JBFVY8SXTV3X&dib=eyJ2IjoiMSJ9.iAUo1kQr3DWMwspj2GhmD4AlGTICZmW1fuI-H009wO_1A8M07T7N_5HEajHsU-b3K550_WStRIL_cN8mGXwmj3qm1dIgNujTszF5pa56VkJkF55dqVsqZCgEmExWmhv81gxP-7V0lQmkDyaR6WqpHqlw-vKjBhwB51e0dhA_8Ta7IkjGQpM2Ql8lXtoMFql1ymht1q59vqKrhpkURVPIHWCrpyC_CUmctNbfR07HEFU.2syORVeKPiqgcmTQA4nRMS2x6L_V7v4kY2bbSJYUgbw&dib_tag=se&keywords=the+house+of+cavanaugh&qid=1735506991&sprefix=the+house+of+cavanaugh%2Caps%2C197&sr=8-1",
        powells:
          "https://www.powells.com/book/the-house-of-cavanaugh-9781960573469?condition=New",
        target:
          "https://www.target.com/p/the-house-of-cavanaugh-by-polly-dugan-paperback/-/A-94409601",
      },
      hook: `What if every belief you’ve ever held about your family was disproven?
        How do you forgive someone who is no longer alive to atone? How do two
        families integrate a staggering discovery into their intertwined
        histories?`,
      bookPraise: [
        {
          quote:
            "<em>The House of Cavanaugh</em> is a moving, elegantly told novel about life and death, about familial love and familial secrets, and about how the past persists in the present, no matter how much we'd like to think otherwise. Polly Dugan is a wise and sensitive writer, and with this beautiful novel she'll break your heart.",
          attribution:
            "—Edan Lepucki, bestselling author of <em>Time's Mouth</em> and <em>California</em>",
          quotePrefix: true,
          quoteSuffix: true,
        },
        {
          quote:
            "What a thrill to read Polly Dugan’s gorgeous new novel about a long-held secret that ruptures and binds two families over decades. There’s so much to admire about <em>The House of Cavanaugh</em>: the depth and complexity of characterization, the fluid movement between perspectives and over time, the surprising revelations. In the end, what makes the book so moving to me is that despite the range of painful emotions it explores—grief, betrayal, resentment—what wins out above all else is love, which remains when everything else fades away.",
          attribution: "—Scott Nadelson, author of <em>Trust Me</em>",
          quotePrefix: true,
          quoteSuffix: true,
        },
        {
          quote:
            "Polly Dugan writes about the complexities of family and marriage, love and loss, with authenticity and deep compassion. This artfully layered novel succeeds at being both a compelling page-turner and a sensitive, nuanced portrait of real people living real lives. I devoured it with eagerness and admiration.",
          attribution: "–Elise Juska, author of <em>Reunion</em>",
          quotePrefix: true,
          quoteSuffix: true,
        },
        {
          quote:
            "I was caught up in this rich, engrossing family saga from the first page to the last, wondering how withheld secrets would transform these relatable characters’ lives. The story unfolds naturally and is told with great empathy for all; no one is a villain, no one a true deceiver. The Cavanaughs are simply alive, and as flawed and immutably human as the rest of us.",
          attribution:
            "—Laura Sims, author of <em>Looker</em> and <em>How Can I Help You</em>",
          quotePrefix: true,
          quoteSuffix: true,
        },
        {
          quote:
            "I love family sagas and <em>The House of Cavanaugh</em> hits all the right notes. Polly Dugan has again proven to be an excellent architect family stories, written with love, humility, and wisdom. This wonderful novel will bring tears to your eyes and make you laugh out loud, and you will not easily or quickly forget this book or these characters. Truly a treasure, a fabulous read.",
          attribution:
            "—Laura L. Engel, author of <em>You’ll Forget This Ever Happened</em> – <em>Secrets, Shame and Adoption in the 1960s</em>",
          quotePrefix: true,
          quoteSuffix: true,
        },
        {
          quote:
            "<em>The House of Cavanaugh</em> is a beautifully wrought portrait of hidden truths carve pathways through generations. Elegantly written written and deeply moving, it considers the quiet devastations caused secrets kept and the redemptive power of truths finally told. A poignant and fearless exploration of love, loss, and the complexities of family.",
          attribution:
            "—Emily Raymond, <em>NYT</em> bestselling coauthor of <em>First Love</em>",
          quotePrefix: true,
          quoteSuffix: true,
        },
        {
          quote:
            "I have been a fan of Polly Dugan's writing since I read <em>So Much a Part of You</em> - she is a master at peeling back the layers of complex interpersonal dynamics and cutting to the emotional core of the things we hide and hold onto. I was so happy to see a family from that collection come back to fill the pages of <em>The House of Cavanaugh</em>, so that I could take a deeper dive relationships that Polly untangles - years of secrets and the subsequent revelations - and again I am held in the raw honesty and sharp truth of a beautiful storyteller. This novel will live with me for a very long time.",
          attribution:
            "–Jodi Angel, author of <em>Biggest Little Girl</em> and <em>You Only Get Letters from Jail</em>.",
          quotePrefix: true,
          quoteSuffix: true,
        },
        {
          quote:
            "<em>The House of Cavanaugh</em> is a knockout. One of the most emotionally astute books I've read in years. How many complex relationships can be explored in one short novel? I think Polly Dugan redefines the answer.",
          attribution:
            "—Susan Perabo, author of <em>The Fall of Lisa Bellow</em>",
          quotePrefix: true,
          quoteSuffix: true,
        },
        {
          quote:
            "<em>The House of Cavanaugh</em> is a brilliant, gripping novel built around the secrets we keep from the world, and the ones we keep from ourselves. Polly Dugan writes like a dream. Her sentences are piercing and true. This book takes clean aim at the messy truths of the human heart. I couldn’t put it down.",
          attribution:
            "–Steve Almond, author of <em>All the Secrets of the World</em> and <em>Truth Is the Arrow, Mercy Is the Bow</em>",
          quotePrefix: true,
          quoteSuffix: true,
        },
      ],
    };
  },
};
</script>
